import React, { useEffect, useState } from "react";
import { Crown } from "./CrownIcon";
import { Flame, MessageCircleQuestion, Trophy } from "lucide-react";
import { ledgerTopOfWeek } from "../lib/api";

export const Hero = ({ onWatch, onAsk }) => {
    const [topFan, setTopFan] = useState(null);
    useEffect(() => {
        ledgerTopOfWeek()
            .then(setTopFan)
            .catch(() => setTopFan(null));
    }, []);

    const scrollToLedger = () => {
        document
            .getElementById("ledger")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <section data-testid="hero-section" className="relative overflow-hidden">
            {/* Splatters */}
            <div
                aria-hidden
                className="absolute -top-24 -left-16 w-[28rem] h-[28rem] rounded-full bg-hotpink/30 blur-3xl"
            />
            <div
                aria-hidden
                className="absolute -bottom-32 right-0 w-[32rem] h-[32rem] rounded-full bg-skyblue/25 blur-3xl"
            />
            <div
                aria-hidden
                className="absolute top-1/3 right-1/4 w-72 h-72 rounded-full bg-kickgreen/15 blur-3xl"
            />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 md:pt-24 md:pb-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-3 mb-6 border-2 border-kickgreen px-4 py-1.5 bg-black">
                            <span
                                className="w-2 h-2 bg-kickgreen rounded-full animate-pulse"
                                aria-hidden
                            />
                            <span className="font-anton uppercase tracking-[0.25em] text-kickgreen text-xs">
                                Streaming on Kick
                            </span>
                        </div>

                        <div className="relative inline-block mb-2">
                            <Crown
                                className="absolute -top-10 -left-2 w-16 h-12 -rotate-12"
                                color="#FF2E93"
                            />
                            <h1
                                data-testid="hero-title"
                                className="font-anton uppercase tracking-tight leading-[0.85] text-white text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem] stamp"
                            >
                                Get
                                <br />
                                <span className="text-skyblue">Saved</span>
                                <br />
                                Bro
                            </h1>
                        </div>

                        <p
                            className="font-graffiti text-hotpink text-3xl md:text-5xl -rotate-2 mt-4 mb-6"
                            data-testid="hero-handle"
                        >
                            @leonardthepreacher
                        </p>

                        <p className="font-chivo text-base md:text-lg text-white/80 max-w-xl leading-relaxed mb-8">
                            Real talk. Real testimony. Real loud. Tap in every night at{" "}
                            <span className="text-kickgreen font-bold">12:00 AM EST</span>{" "}
                            for the most unfiltered street ministry on Kick. Watch the
                            stream, chop it up in chat, slide in the DMs.
                        </p>

                        {topFan && (
                            <button
                                data-testid="hero-top-fan-callout"
                                onClick={scrollToLedger}
                                title="See the Top Advocates leaderboard"
                                className="group relative w-full sm:max-w-md mb-7 text-left bg-black border-2 border-kickgreen px-4 py-3 -rotate-[0.6deg] hover:rotate-0 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_#53FC18] transition-all"
                            >
                                <div className="absolute -top-3 -left-2 bg-kickgreen text-black font-anton uppercase text-[10px] tracking-widest px-2 py-0.5 border-2 border-black rotate-[-4deg]">
                                    Top Fan · This Week
                                </div>
                                <div className="flex items-center gap-3">
                                    <Trophy
                                        className="w-7 h-7 text-kickgreen group-hover:scale-110 transition-transform"
                                        strokeWidth={2.5}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="font-graffiti text-skyblue text-xl leading-none truncate -rotate-1">
                                            @{topFan.handle}
                                        </div>
                                        <div className="font-chivo text-xs text-white/60 mt-1">
                                            stacked{" "}
                                            <span className="text-kickgreen font-bold">
                                                {topFan.points} pts
                                            </span>{" "}
                                            in the last 7 days · join &amp; stack yours →
                                        </div>
                                    </div>
                                    <Crown
                                        className="w-8 h-6 group-hover:rotate-12 transition-transform"
                                        color="#FF2E93"
                                    />
                                </div>
                            </button>
                        )}

                        <div className="flex flex-wrap gap-4">
                            <button
                                data-testid="hero-watch-btn"
                                onClick={onWatch}
                                className="inline-flex items-center gap-2 bg-kickgreen text-black font-anton uppercase tracking-wider text-lg md:text-xl px-7 py-4 border-2 border-black hover:-translate-y-1 hover:shadow-[8px_8px_0_#FF2E93] transition-all"
                            >
                                <Flame className="w-5 h-5" strokeWidth={3} />
                                Watch the Stream
                            </button>
                            <button
                                data-testid="hero-ask-btn"
                                onClick={onAsk}
                                className="inline-flex items-center gap-2 bg-transparent text-white font-anton uppercase tracking-wider text-lg md:text-xl px-7 py-4 border-2 border-skyblue hover:bg-skyblue/10 hover:-translate-y-1 hover:shadow-[8px_8px_0_#0AC3FF] transition-all"
                            >
                                <MessageCircleQuestion className="w-5 h-5" strokeWidth={3} />
                                Ask a Question
                            </button>
                        </div>
                    </div>

                    {/* Right side card stack */}
                    <div className="lg:col-span-5 relative">
                        <div className="relative">
                            <img
                                data-testid="hero-portrait"
                                src="/preacher-portrait.png"
                                alt="Leonard The Preacher — GTA-style portrait"
                                className="relative z-10 w-full max-w-md mx-auto border-4 border-black brutal-shadow-blue rotate-[-2deg] hover:rotate-0 transition-transform duration-500"
                                loading="eager"
                            />
                            <div
                                aria-hidden
                                className="absolute -top-4 -left-4 z-20 bg-kickgreen text-black font-anton uppercase text-xs tracking-widest px-3 py-1 border-2 border-black rotate-[-6deg]"
                            >
                                Live ✦ Get Saved Bro
                            </div>
                            <div
                                aria-hidden
                                className="absolute -bottom-4 -right-2 z-20 bg-hotpink text-white font-anton uppercase text-xs tracking-widest px-3 py-1 border-2 border-black rotate-[4deg]"
                            >
                                @leonardthepreacher
                            </div>
                        </div>
                        <div
                            aria-hidden
                            className="absolute -bottom-6 -right-4 font-graffiti text-skyblue text-3xl rotate-[8deg] select-none pointer-events-none"
                        >
                            stay tuned ✝
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const toneMap = {
    green: "text-kickgreen",
    pink: "text-hotpink",
    blue: "text-skyblue",
};

// eslint-disable-next-line no-unused-vars
const Stat = ({ label, value, tone }) => (
    <div>
        <div className={`font-anton text-3xl leading-none ${toneMap[tone]}`}>
            {value}
        </div>
        <div className="font-chivo text-[10px] uppercase tracking-widest text-white/50 mt-1">
            {label}
        </div>
    </div>
);
